import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BackendClient, type BackendTypes, GraphQLClient, gql } from '@tf/api';
import { T } from '@tf/utils';

import { useOverviewData } from '@/core/api/overview';
import { getAccountQuery } from '@/core/queries';

export const accountQueryOptions = (accountId: number | null) => {
	return queryOptions({
		queryKey: ['accounts', accountId],
		queryFn: () => getAccountQuery(accountId!),
		staleTime: Infinity,
		enabled: !!accountId && accountId > 0,
		refetchOnWindowFocus: false,
	});
};

export const useAccount = (accountId: number | null) => {
	return useQuery(accountQueryOptions(accountId));
};

export const useCreateAccountMutation = () => {
	const { data } = useOverviewData();
	const tenantNodeId = data?.tenantNodeId;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			accountKind,
			entitySelfSegmentKind,
			entitySelfSegmentData,
			requiredConnections,
		}: {
			accountKind: BackendTypes.EntityKind;
			entitySelfSegmentKind: BackendTypes.SegmentKind;
			entitySelfSegmentData: Record<string, any>;
			requiredConnections?: BackendTypes.RequiredConnectionSelection[];
		}): Promise<BackendTypes.EntityConnectNewData> => {
			T.assertNotNullish(tenantNodeId, 'overview not loaded');

			const res = await BackendClient.getInstance().entityConnectNew(
				{ parentEntityId: tenantNodeId, connectionKind: 'ACCOUNT', entityKind: accountKind },
				{
					listEntitySegments: [
						{
							// @ts-expect-error No need for graphId here
							segmentIdentity: {
								segmentKind: entitySelfSegmentKind,
							},
							segment: entitySelfSegmentData,
						},
						{
							// @ts-expect-error No need for graphId here
							segmentIdentity: {
								segmentKind: 'COMMON__RM_FEEDBACK',
							},
							segment: { client_account_status: 'PROSPECT' },
						},
					],
					listConnectionSegments: [],
					requiredConnections,
				},
			);

			return res.json();
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['overviewAccounts'] });
		},
	});
};

export type ProfileConsent = {
	meta: {
		signer: {
			'@id': string;
			email?: string;
		};
		signed_at: string;
	};
	labels: {
		label: string;
	};
	data: {
		description: string;
	};
};

const profileConsentQueryOptions = (profileId: string) => {
	return queryOptions({
		queryKey: ['profileConsent', profileId],
		queryFn: async (): Promise<ProfileConsent> => {
			const res = await GraphQLClient.getInstance().request(
				gql`
					query ProfileConsent($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "consent"
								meta: { AND: [{ key: "/@relations/profile", operator: HAS_KEY, value: $profileId }] }
							}
							paginationFilter: { limit: 1, offset: 0, sortByMeta: "/@createdAt", sortByDirection: DESC }
						) {
							id
							meta(path: ["/signer", "/signed_at"])
							labels(path: ["/label"])
							data(path: ["/description"])
						}
					}
				`,
				{ profileId },
			);

			return res.get[0];
		},
	});
};

export const useProfileConsentQuery = (profileId: string) => {
	return useQuery(profileConsentQueryOptions(profileId));
};
